import type { ComponentType } from 'react'
import {
  LayoutDashboard,
  LineChart,
  Search,
  Database,
  Rocket,
  Target,
  ListChecks,
  Settings,
  GraduationCap,
  LifeBuoy,
  Calculator,
  Radar,
  Lightbulb,
  Activity,
  CircleDollarSign,
  Star,
  Users,
  Anchor,
  List,
  ShoppingCart,
  Receipt,
  TrendingUp,
  Heart,
  Bell,
  Globe,
  Flag,
  Newspaper,
} from 'lucide-react'

function ExtensionIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M10 5V3a2 2 0 1 1 4 0v2h3a2 2 0 0 1 2 2v3h2a2 2 0 1 1 0 4h-2v3a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3Z" />
    </svg>
  )
}

function KaiIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12 2C8 2 5 7.5 5 13a7 7 0 0 0 14 0c0-5.5-3-11-7-11Z" />
      <path d="M10.5 4l2.5 3.5-4 3.5 3.5 3.5-3 3" />
    </svg>
  )
}

export type RouteStatus = 'planned' | 'in-progress' | 'placeholder' | 'ready'

export interface AppRoute {
  path: string
  label: string
  icon: ComponentType<{ className?: string }>
  status: RouteStatus
  description?: string
  adminOnly?: boolean
  proOnly?: boolean
  externalUrl?: string
  modalAction?: boolean
  /**
   * #770: sidebar'da "Yeni" rozeti — bu ISO tarihin (YYYY-MM-DD, Europe/Istanbul
   * gün sonu dahil) SONUNA kadar gösterilir. Bkz. `lib/nav-badge.ts` isNavItemNew.
   */
  newUntil?: string
}

export const appRoutes: AppRoute[] = [
  {
    path: '/app/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    status: 'in-progress',
    description: 'KPI kartları, product snapshot, detay grafikleri',
  },
  {
    path: '/app/rank-tracker',
    label: 'Rank Tracker',
    icon: LineChart,
    status: 'in-progress',
    description: 'Satır genişlemeli keyword görünümü, heatmap / line chart',
    proOnly: true,
  },
  {
    path: '/app/deepsearch',
    label: 'Deepsearch',
    icon: Search,
    status: 'in-progress',
    description: 'Kategori seçimi, preset filtreler ve sonuç tablosu',
    proOnly: true,
  },
  {
    path: '/app/freqfinder',
    label: 'FreqFinder',
    icon: Radar,
    status: 'ready',
    description: 'Hızlı keyword arama',
    proOnly: true,
  },
  {
    path: '/app/key-insight',
    label: 'KeyInsight',
    icon: Lightbulb,
    status: 'ready',
    description: 'Keyword ve ASIN analizi',
    proOnly: true,
  },
  {
    path: '/app/keyword-lists',
    label: 'Keyword Lists',
    icon: List,
    status: 'ready',
    description: 'Keyword listeleri oluştur ve yönet',
    proOnly: true,
  },
  {
    path: '/app/product-database',
    label: 'Product Database',
    icon: Database,
    status: 'ready',
    description: 'Amazon ürün veritabanı arama ve filtreleme',
    proOnly: true,
    adminOnly: true,
  },
  {
    path: '/app/product-list',
    label: 'Favoriler',
    icon: Heart,
    status: 'ready' as const,
    description: 'Extension ve platformda favorilenen ürünler ve kelimeler',
    proOnly: true,
  },
  {
    path: '/app/new-release',
    label: 'New Release',
    icon: Rocket,
    status: 'ready',
    description: 'Amazon New Releases tracking ve filtreleme',
    proOnly: true,
  },
  {
    path: '/app/sqp',
    label: 'SQP',
    icon: Target,
    status: 'ready',
    description: 'Arama sorgusu performans analizi',
    proOnly: true,
    adminOnly: true,
  },
  {
    path: '/app/listing-builder',
    label: 'Freq Listing Builder',
    icon: ListChecks,
    status: 'ready',
    description: 'Amazon listing oluşturma ve optimizasyon',
    proOnly: true,
  },
  {
    path: '/app/settings',
    label: 'Ayarlar',
    icon: Settings,
    status: 'planned',
  },
  {
    path: '/app/admin/allowed-emails',
    label: 'Kullanıcı Yönetimi',
    icon: Users,
    status: 'ready',
    description: 'Allowed emails yönetim paneli',
    adminOnly: true,
  },
  {
    path: '/app/admin/user-activity',
    label: 'Kullanıcı Aktivitesi',
    icon: Activity,
    status: 'ready',
    description: 'Aktif oturumlar (kim online) + giriş geçmişi',
    adminOnly: true,
  },
  {
    path: '/app/admin/anchor-review',
    label: 'Anchor Review',
    icon: Anchor,
    status: 'ready',
    description: 'Sales estimation anchor kontrol paneli',
    adminOnly: true,
  },
  {
    path: '/app/admin/academy',
    label: 'Akademi Yönetimi',
    icon: GraduationCap,
    status: 'ready',
    description: 'Akademi içerik yönetim paneli',
    adminOnly: true,
  },
  {
    path: '/app/admin/content',
    label: 'İçerik Yönetimi',
    icon: Newspaper,
    status: 'ready',
    description: 'Akademi ve yazı içeriklerinin yönetim girişi',
    adminOnly: true,
  },
  {
    path: '/app/admin/content/blog',
    label: 'Blog Yönetimi',
    icon: Newspaper,
    status: 'ready',
    description: 'Blog yazılarının durumu ve blog erişimi (salt görüntüleme)',
    adminOnly: true,
  },
  {
    path: '/app/admin/notifications',
    label: 'Bildirim Gönder',
    icon: Bell,
    status: 'ready',
    description: 'Kullanıcılara bildirim gönder ve grup yönet',
    adminOnly: true,
  },
  {
    path: '/app/admin/scraping-gate',
    label: 'ABD Dışı Tarama',
    icon: Globe,
    status: 'ready',
    description: 'ScraperAPI ABD-dışı tarama anahtarı ve kredi durumu',
    adminOnly: true,
  },
  {
    path: '/app/admin/keyword-score-lab',
    label: 'Skor Laboratuvarı',
    icon: Calculator,
    status: 'ready',
    description: 'Kelime yaz, Sensei skorunu anında hesapla ve ağırlıklarla dene',
    adminOnly: true,
  },
  {
    path: '/app/admin/extension-features',
    label: 'Erişim yönetimi',
    icon: Flag,
    status: 'ready',
    description: 'Sayfaların ve eklenti özelliklerinin kimlere açık olduğunu yönet',
    adminOnly: true,
  },
  {
    path: '/app/extension',
    label: 'Extension',
    icon: ExtensionIcon,
    status: 'ready',
    description: 'Sensei Chrome Extension',
    externalUrl: 'https://chromewebstore.google.com/detail/sensei-amazon-fba-extensi/bgfekggimpppipknjhmkfmjlhommmopl?authuser=0&hl=tr',
  },
  {
    path: '/app/academy',
    label: 'Akademi',
    icon: GraduationCap,
    status: 'ready',
    proOnly: true,
    newUntil: '2026-10-08',
  },
  {
    path: '/app/support',
    label: 'Yardım ve Destek',
    icon: LifeBuoy,
    status: 'ready',
    modalAction: true,
  },
  {
    path: '/app/chatbot',
    label: 'Kai',
    icon: KaiIcon,
    status: 'ready',
  },
  {
    path: '/app/sales-estimator',
    label: 'Sales Estimator',
    icon: Calculator,
    status: 'ready',
    description: 'BSR veya ASIN ile satış tahmini',
    proOnly: true,
  },
  {
    path: '/app/profit-calculator',
    label: 'Profit Calculator',
    icon: CircleDollarSign,
    status: 'ready',
    description: 'FBA ürün karlılık hesaplama',
    proOnly: true,
  },
  {
    path: '/app/review-automation',
    label: 'Review Automation',
    icon: Star,
    status: 'planned',
    proOnly: true,
  },
  {
    path: '/app/amazon/dashboard',
    label: 'Amazon P&L',
    icon: ShoppingCart,
    status: 'in-progress',
    proOnly: true,
    description: 'Amazon satış, trafik ve envanter verileri',
  },
  {
    path: '/app/amazon/advertising',
    label: 'Advertising',
    icon: TrendingUp,
    status: 'in-progress',
    proOnly: true,
    adminOnly: true,
    description: 'Reklam kampanyaları, TACoS ve PPC analizi',
  },
  {
    path: '/app/billing',
    label: 'Billing',
    icon: Receipt,
    status: 'ready',
    description: 'Subscription management and invoices',
  },
]
